import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import ScaleLoader from 'react-spinners/ScaleLoader';


import JukeboxVideoRow from './JukeboxVideoRow';
import { formatPrice } from './BuyBumps';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { authContext } from '../contexts/AuthContext';
import { isFillerVideo } from '../utils/Filler';
import { ADMIN_MESSAGE_MAX_LENGTH, ADMIN_MESSAGE_MAX_SECONDS, ADMIN_MESSAGE_MIN_SECONDS } from '../utils/AdminMessages';

import './JukeboxView.scss';
import './AdminView.scss';
import AppHeader from './AppHeader';
import FooterContent from './FooterContent';

// The shapes src/helpers/ownerDashboard.ts sends.
interface Money {
  sales: number;
  gross: number;
  venueShare: number;
  stripeFees: number;
  net: number;
  storeSales: number;
}

interface PlayerRow {
  id: number;
  name: string;
  playerIdentifier: string;
  owner: { id: number; username: string } | null;
  queued: number;
  nowPlaying: { title: string; thirdPartyId: string } | null;
  lastActive: string;
  allTime: Money;
  thisMonth: Money;
}

interface Overview {
  currency: string;
  stripeFeePercent: number;
  stripeFeeFixed: number;
  allTime: Money;
  thisMonth: Money;
  unattributed: { allTime: Money; thisMonth: Money };
  byPlatform: { platform: string; sales: number }[];
  players: PlayerRow[];
  users: number;
  shoutouts?: { allTime: Money; thisMonth: Money };
  plus?: {
    price: number;
    members: number;
    monthlyRecurring: number;
    allTime: Money;
    thisMonth: Money;
    list: { id: number; username: string; email: string; plusUntil: string }[];
  };
}

interface UserMatch {
  id: number;
  username: string;
  email: string;
  role: string;
  availableBumps: number;
  availableSuperBumps: number;
  createdAt: string;
  players: number;
  spend: Money;
  /** Banned from buying shout-outs anywhere since then, or null. */
  shoutoutBannedAt?: string | null;
  /** A Jookable+ member until then, or null. */
  plusUntil?: string | null;
  /** Two-step sign-in on since then, or null. */
  mfaEnabledAt?: string | null;
}

interface Purchase {
  soldAt: string;
  platform: string;
  productName: string;
  amount: number | null;
  player: { id: number; name: string; playerIdentifier: string } | null;
}

interface BumpChange {
  id: number;
  adminUsername: string;
  oldBumps: number;
  newBumps: number;
  oldSuperBumps: number;
  newSuperBumps: number;
  note: string;
  createdAt: string;
}

interface RankedUser {
  rank: number;
  id: number;
  username: string;
  email: string;
  plays: number;
  spend: Money;
  topPlayer: { id: number; name: string; playerIdentifier: string; plays: number } | null;
}

interface Rankings {
  bySongsPlayed: RankedUser[];
  bySpend: RankedUser[];
  playsSince: string | null;
}

/** A message sent to the TVs, as src/helpers/adminMessages.ts lists it. */
interface SentMessage {
  id: number;
  message: string;
  everyone: boolean;
  seconds: number;
  msLeft: number;
  player: { id: number; name: string; playerIdentifier: string } | null;
  sentBy: string | null;
  createdAt: string;
}

type Tab = 'revenue' | 'players' | 'users' | 'rankings' | 'messages' | 'reload';
const TAB_NAMES: { [name in Tab]: string } = {
  revenue: 'Revenue',
  players: 'Jukeboxes',
  users: 'Users',
  rankings: 'Rankings',
  messages: 'Messages',
  reload: 'Reload',
};
const EVERY_JUKEBOX = 'all';
const DEFAULT_MESSAGE_SECONDS = '15';
type RankBy = 'songs' | 'spend';
type Period = 'thisMonth' | 'allTime';

const PLATFORM_NAMES: { [platform: string]: string } = { web: 'Web', ios: 'iPhone', android: 'Android' };
const platformName = (platform: string) => PLATFORM_NAMES[platform] || platform;

const shortDate = (iso: string) => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const agoText = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(minutes)) return '';
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return shortDate(iso);
};

/** Four figures for one period: what came in, who took what, and what Jookable keeps. */
const MoneyFigures = ({ money, currency }: { money: Money; currency: string }) => (
  <div className="admin-figures">
    <div className="admin-figure admin-figure-main">
      <span className="admin-figure-value">{formatPrice(money.net, currency)}</span>
      <span className="admin-figure-label">Jookable keeps</span>
    </div>
    <div className="admin-figure">
      <span className="admin-figure-value">{formatPrice(money.gross, currency)}</span>
      <span className="admin-figure-label">Sales</span>
    </div>
    <div className="admin-figure">
      <span className="admin-figure-value">{formatPrice(money.venueShare, currency)}</span>
      <span className="admin-figure-label">Venues' cut</span>
    </div>
    <div className="admin-figure">
      <span className="admin-figure-value">{formatPrice(money.stripeFees, currency)}</span>
      <span className="admin-figure-label">Stripe fees</span>
    </div>
  </div>
);

/** One ranking: the top accounts by songs played or by money spent, with their usual jukebox. */
const RankingTable = ({ users, rankBy, money }: { users: RankedUser[]; rankBy: RankBy; money: (cents: number) => string }) => {
  if (users.length === 0) {
    return <p className="jukebox-empty">{rankBy === 'songs' ? 'No songs played yet.' : 'No purchases yet.'}</p>;
  }
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>User</th>
            <th>Jukebox</th>
            <th className="num">{rankBy === 'songs' ? 'Songs' : 'Spent'}</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td className="num admin-rank">{user.rank}</td>
              <td>
                <div className="admin-cell-main">{user.username}</div>
                <div className="admin-cell-sub">{user.email}</div>
              </td>
              <td>
                {user.topPlayer ? (
                  <>
                    <div className="admin-cell-main">{user.topPlayer.name}</div>
                    <div className="admin-cell-sub">
                      {user.topPlayer.playerIdentifier.toUpperCase()} · {user.topPlayer.plays}{' '}
                      {user.topPlayer.plays === 1 ? 'song' : 'songs'}
                    </div>
                  </>
                ) : (
                  <div className="admin-cell-sub">None yet</div>
                )}
              </td>
              <td className="num">
                {rankBy === 'songs' ? (
                  user.plays
                ) : (
                  <>
                    <div>{money(user.spend.gross)}</div>
                    {user.spend.storeSales > 0 && (
                      <div className="admin-cell-sub">+{user.spend.storeSales} in app stores</div>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * The owner's dashboard: Jookable as a whole rather than one jukebox. Revenue in total and
 * per jukebox, every jukebox's queue, and a lookup of accounts with what they have spent.
 * Admin accounts only; the server refuses everyone else.
 */
const AdminView = () => {
  const { auth, currentUser } = useContext(authContext);
  const token = auth && auth.token;

  const [tab, setTab] = useState<Tab>('revenue');
  const [period, setPeriod] = useState<Period>('thisMonth');
  const [overview, setOverview] = useState<Overview>(null);
  const [loadError, setLoadError] = useState<string>(null);
  const [forbidden, setForbidden] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [openPlayerId, setOpenPlayerId] = useState<number>(null);
  const [queue, setQueue] = useState<any>(null);
  const [queueError, setQueueError] = useState<string>(null);

  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<UserMatch[]>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string>(null);
  const [openUserId, setOpenUserId] = useState<number>(null);
  const [purchases, setPurchases] = useState<Purchase[]>(null);
  const [bumpChanges, setBumpChanges] = useState<BumpChange[]>(null);
  const [bumpsInput, setBumpsInput] = useState('');
  const [superBumpsInput, setSuperBumpsInput] = useState('');
  const [bumpNote, setBumpNote] = useState('');
  const [savingBumps, setSavingBumps] = useState(false);
  const [bumpsError, setBumpsError] = useState<string>(null);
  const [bumpsSaved, setBumpsSaved] = useState(false);

  const [rankBy, setRankBy] = useState<RankBy>('songs');
  const [rankings, setRankings] = useState<Rankings>(null);
  const [rankingsError, setRankingsError] = useState<string>(null);

  // Free messages to the TVs: one jukebox, or every jukebox at once.
  const [sentMessages, setSentMessages] = useState<SentMessage[]>(null);
  const [messagesError, setMessagesError] = useState<string>(null);
  const [messageTarget, setMessageTarget] = useState(EVERY_JUKEBOX);
  const [messageText, setMessageText] = useState('');
  const [messageSeconds, setMessageSeconds] = useState(DEFAULT_MESSAGE_SECONDS);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendError, setSendError] = useState<string>(null);
  const [messageSent, setMessageSent] = useState<string>(null);

  const loadMessages = () =>
    api
      .admin('messages', 'GET', token, null)
      .then(response => {
        setSentMessages(response.messages);
        setMessagesError(null);
      })
      .catch(error => setMessagesError(error.message));

  const sendMessage = (event: React.FormEvent) => {
    event.preventDefault();
    const seconds = messageSeconds.trim();
    if (!/^[0-9]+$/.test(seconds) || Number(seconds) < ADMIN_MESSAGE_MIN_SECONDS || Number(seconds) > ADMIN_MESSAGE_MAX_SECONDS) {
      setSendError(`Choose between ${ADMIN_MESSAGE_MIN_SECONDS} and ${ADMIN_MESSAGE_MAX_SECONDS} seconds.`);
      return;
    }
    const everyone = messageTarget === EVERY_JUKEBOX;
    if (everyone && !window.confirm('Show this on every jukebox?')) return;
    setSendingMessage(true);
    setSendError(null);
    setMessageSent(null);
    api
      .admin('messages', 'POST', token, {
        playerId: everyone ? null : Number(messageTarget),
        message: messageText,
        seconds: Number(seconds),
      })
      .then(response => {
        trackEvent('admin_message_sent', { target: everyone ? 'every_jukebox' : 'one_jukebox', seconds: Number(seconds) });
        setSentMessages(response.messages);
        setMessageText('');
        setMessageSent(everyone ? 'Sent to every jukebox.' : `Sent to ${response.sent.player ? response.sent.player.name : 'the jukebox'}.`);
      })
      .catch(error => setSendError(error.message))
      .then(() => setSendingMessage(false));
  };

  // Reloading the screens on one jukebox, or on every jukebox (POST /v1/admin/reload).
  const [reloadTarget, setReloadTarget] = useState(EVERY_JUKEBOX);
  const [reloading, setReloading] = useState(false);
  const [reloadResult, setReloadResult] = useState<string>(null);
  const [reloadError, setReloadError] = useState<string>(null);

  const reloadScreens = (target: string) => {
    const everyone = target === EVERY_JUKEBOX;
    const jukebox = everyone ? null : overview && overview.players.find(player => String(player.id) === target);
    const where = everyone ? 'every jukebox' : jukebox ? jukebox.name : 'this jukebox';
    if (!window.confirm(`Reload the screens on ${where}?`)) return;
    setReloading(true);
    setReloadError(null);
    setReloadResult(null);
    api
      .admin('reload', 'POST', token, { playerId: everyone ? null : Number(target) })
      .then((response: { jukeboxes: number; screens: number }) => {
        if (response.screens === 0) {
          setReloadResult(everyone ? 'No screens are connected right now, so nothing reloaded.' : `No screens are connected to ${where} right now, so nothing reloaded.`);
          return;
        }
        const screens = response.screens === 1 ? '1 screen' : `${response.screens} screens`;
        const jukeboxes = response.jukeboxes === 1 ? '1 jukebox' : `${response.jukeboxes} jukeboxes`;
        setReloadResult(`Reloading ${screens} on ${jukeboxes}.`);
      })
      .catch(error => setReloadError(error.message))
      .then(() => setReloading(false));
  };

  const stopMessage = (message: SentMessage) =>
    api
      .admin(`messages/${message.id}/stop`, 'POST', token, {})
      .then(response => setSentMessages(response.messages))
      .catch(error => {
        setMessagesError(error.message);
        loadMessages();
      });

  const loadOverview = () => {
    setRefreshing(true);
    return api
      .admin('overview', 'GET', token, null)
      .then((response: Overview) => {
        setOverview(response);
        setLoadError(null);
      })
      .catch(error => {
        if (error.status === 403) setForbidden(true);
        else setLoadError(error.message);
      })
      .then(() => setRefreshing(false));
  };

  const loadRankings = () =>
    api
      .admin('rankings', 'GET', token, null)
      .then((response: Rankings) => {
        setRankings(response);
        setRankingsError(null);
      })
      .catch(error => setRankingsError(error.message));

  const openTab = (name: Tab) => {
    setTab(name);
    if (name === 'rankings' && !rankings) loadRankings();
    if (name === 'messages') loadMessages();
  };

  const loadQueue = (playerId: number) =>
    api
      .admin(`players/${playerId}`, 'GET', token, null)
      .then(response => {
        setQueue(response);
        setQueueError(null);
      })
      .catch(error => setQueueError(error.message));

  useEffect(() => {
    if (token) loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const togglePlayer = (playerId: number) => {
    setQueue(null);
    setQueueError(null);
    if (openPlayerId === playerId) {
      setOpenPlayerId(null);
      return;
    }
    setOpenPlayerId(playerId);
    loadQueue(playerId);
  };

  const runSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setOpenUserId(null);
    api
      .admin(`users?q=${encodeURIComponent(search.trim())}`, 'GET', token, null)
      .then(response => {
        setUsers(response.users);
        setSearchError(null);
      })
      .catch(error => setSearchError(error.message))
      .then(() => setSearching(false));
  };

  const toggleUser = (user: UserMatch) => {
    setPurchases(null);
    setBumpChanges(null);
    setBumpsError(null);
    setBumpsSaved(false);
    if (openUserId === user.id) {
      setOpenUserId(null);
      return;
    }
    setOpenUserId(user.id);
    setBumpsInput(String(user.availableBumps));
    setSuperBumpsInput(String(user.availableSuperBumps));
    setBumpNote('');
    api
      .admin(`users/${user.id}/purchases`, 'GET', token, null)
      .then(response => setPurchases(response.purchases))
      .catch(() => setPurchases([]));
    api
      .admin(`users/${user.id}/bumps`, 'GET', token, null)
      .then(response => setBumpChanges(response.changes))
      .catch(() => setBumpChanges([]));
  };

  // Lifts the site-wide shout-out ban set after bans on players of three owners. Their bans
  // on single players stay. The server logs who lifted it.
  const [liftingBan, setLiftingBan] = useState(false);
  const [banError, setBanError] = useState<string>(null);
  const liftShoutoutBan = (user: UserMatch) => {
    if (!window.confirm(`Let ${user.username} buy shout-outs again? Their bans on single players stay.`)) return;
    setLiftingBan(true);
    setBanError(null);
    api
      .admin(`users/${user.id}/shoutout-unban`, 'POST', token, {})
      .then(() => setUsers(list => (list || []).map(row => (row.id === user.id ? { ...row, shoutoutBannedAt: null } : row))))
      .catch(error => setBanError(error.message))
      .then(() => setLiftingBan(false));
  };

  // Turns two-step sign-in off for someone who lost their phone and their recovery codes.
  // Their sessions end and they are emailed. Only after checking it really is them.
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState<string>(null);
  const turnOffMfa = (user: UserMatch) => {
    if (!window.confirm(`Turn off two-step sign-in for ${user.username}? Only do this once you are sure it is really them.`)) return;
    setMfaBusy(true);
    setMfaError(null);
    api
      .admin(`users/${user.id}/mfa/disable`, 'POST', token, {})
      .then(() => setUsers(list => (list || []).map(row => (row.id === user.id ? { ...row, mfaEnabledAt: null } : row))))
      .catch(error => setMfaError(error.message))
      .then(() => setMfaBusy(false));
  };

  // Sets the account's balance to exactly what was typed. The server logs who changed it.
  const saveBumps = (event: React.FormEvent, user: UserMatch) => {
    event.preventDefault();
    const bumps = Number(bumpsInput);
    const superBumps = Number(superBumpsInput);
    if (!/^[0-9]+$/.test(bumpsInput.trim()) || !/^[0-9]+$/.test(superBumpsInput.trim())) {
      setBumpsError('Enter whole numbers, 0 or more.');
      return;
    }
    setSavingBumps(true);
    setBumpsError(null);
    setBumpsSaved(false);
    api
      .admin(`users/${user.id}/bumps`, 'PUT', token, {
        availableBumps: bumps,
        availableSuperBumps: superBumps,
        note: bumpNote.trim(),
      })
      .then(response => {
        setUsers(list =>
          (list || []).map(row =>
            row.id === user.id
              ? { ...row, availableBumps: response.availableBumps, availableSuperBumps: response.availableSuperBumps }
              : row,
          ),
        );
        setBumpChanges(response.changes);
        setBumpNote('');
        setBumpsSaved(true);
      })
      .catch(error => setBumpsError(error.message))
      .then(() => setSavingBumps(false));
  };

  const header = <AppHeader />;

  if (forbidden || (currentUser && currentUser.role !== 'admin')) {
    return (
      <div className="jukebox">
        {header}
        <div className="jukebox-body">
          <div className="jukebox-panel">
            <p className="jukebox-empty">Only Jookable admins can see this page.</p>
            <Link className="jukebox-primary-link" to="/home">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="jukebox-loading">
        <h1>{loadError ? `Could not load the dashboard: ${loadError}` : 'Opening the dashboard'}</h1>
        {!loadError && <ScaleLoader cssOverride={{ display: 'flex' }} height={35} width={4} radius={2} color={'#fff'} loading={true} />}
      </div>
    );
  }

  const currency = overview.currency;
  const money = (cents: number) => formatPrice(cents, currency);
  const storeSales = overview[period].storeSales;
  const earning = overview.players
    .filter(player => player[period].sales > 0)
    .sort((a, b) => b[period].gross - a[period].gross);
  const unattributed = overview.unattributed[period];

  return (
    <div className="jukebox admin">
      {header}

      <div className="jukebox-body">
        <div className="jukebox-panel admin-panel">
          <div className="jukebox-player">
            <div className="jukebox-player-name">Jookable dashboard</div>
            <button
              type="button"
              className="admin-refresh"
              onClick={() => {
                loadOverview();
                if (openPlayerId) loadQueue(openPlayerId);
                if (tab === 'rankings') loadRankings();
                if (tab === 'messages') loadMessages();
              }}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
            </button>
          </div>
          <div className="admin-subtitle">
            {overview.players.length} jukeboxes · {overview.users} accounts
          </div>

          <div className="jukebox-tabs" role="tablist">
            {(Object.keys(TAB_NAMES) as Tab[]).map(name => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={tab === name}
                className={tab === name ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => openTab(name)}
              >
                {TAB_NAMES[name]}
              </button>
            ))}
          </div>

          {tab === 'revenue' && (
            <div>
              <div className="admin-period">
                <button
                  type="button"
                  className={period === 'thisMonth' ? 'admin-chip is-active' : 'admin-chip'}
                  onClick={() => setPeriod('thisMonth')}
                >
                  This month
                </button>
                <button
                  type="button"
                  className={period === 'allTime' ? 'admin-chip is-active' : 'admin-chip'}
                  onClick={() => setPeriod('allTime')}
                >
                  All time
                </button>
              </div>

              <MoneyFigures money={overview[period]} currency={currency} />

              <p className="admin-note">
                {overview[period].sales} sales
                {storeSales > 0 && `, ${storeSales} of them in the app stores and not in these figures`}. Web sales only;
                Stripe fees are an estimate at {overview.stripeFeePercent}% plus {money(overview.stripeFeeFixed)}.
              </p>

              {!!overview.shoutouts && overview.shoutouts[period].sales > 0 && (
                <p className="admin-note">
                  TV shout-outs, not in the figures above: {overview.shoutouts[period].sales} sold,{' '}
                  {money(overview.shoutouts[period].gross)} paid, {money(overview.shoutouts[period].venueShare)} to
                  venues, Jookable keeps about {money(overview.shoutouts[period].net)}.
                </p>
              )}

              {!!overview.plus && (
                <>
                  <h3 className="admin-heading">Jookable+</h3>
                  <div className="admin-figures">
                    <div className="admin-figure admin-figure-main">
                      <span className="admin-figure-value">{overview.plus.members}</span>
                      <span className="admin-figure-label">{overview.plus.members === 1 ? 'Member' : 'Members'} now</span>
                    </div>
                    <div className="admin-figure">
                      <span className="admin-figure-value">{money(overview.plus.monthlyRecurring)}</span>
                      <span className="admin-figure-label">A month, at {money(overview.plus.price)} each</span>
                    </div>
                    <div className="admin-figure">
                      <span className="admin-figure-value">{money(overview.plus[period].gross)}</span>
                      <span className="admin-figure-label">Paid {period === 'thisMonth' ? 'this month' : 'all time'}</span>
                    </div>
                    <div className="admin-figure">
                      <span className="admin-figure-value">{money(overview.plus[period].net)}</span>
                      <span className="admin-figure-label">Jookable keeps</span>
                    </div>
                  </div>
                  <p className="admin-note">
                    {overview.plus[period].sales} membership {overview.plus[period].sales === 1 ? 'payment' : 'payments'}, not in
                    the bump figures above. Venues get no cut of memberships.
                  </p>
                  {overview.plus.list.length > 0 && (
                    <ul className="admin-purchases">
                      {overview.plus.list.map(member => (
                        <li key={member.id} className="admin-purchase">
                          <span className="admin-purchase-name">
                            {member.username}
                            <span className="admin-cell-sub"> {member.email}</span>
                          </span>
                          <span className="admin-purchase-amount">Until {shortDate(member.plusUntil)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              <h3 className="admin-heading">By jukebox</h3>
              {earning.length === 0 && unattributed.sales === 0 ? (
                <p className="jukebox-empty">No sales yet.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Jukebox</th>
                        <th className="num">Sales</th>
                        <th className="num">Venue</th>
                        <th className="num">Keeps</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earning.map(player => (
                        <tr key={player.id}>
                          <td>
                            <div className="admin-cell-main">{player.name}</div>
                            <div className="admin-cell-sub">
                              {player.playerIdentifier.toUpperCase()}
                              {player.owner && ` · ${player.owner.username}`}
                            </div>
                          </td>
                          <td className="num">{money(player[period].gross)}</td>
                          <td className="num">{money(player[period].venueShare)}</td>
                          <td className="num">{money(player[period].net)}</td>
                        </tr>
                      ))}
                      {unattributed.sales > 0 && (
                        <tr>
                          <td>
                            <div className="admin-cell-main">Not tied to a jukebox</div>
                            <div className="admin-cell-sub">Venue not set up for payouts, or bought from home</div>
                          </td>
                          <td className="num">{money(unattributed.gross)}</td>
                          <td className="num">{money(unattributed.venueShare)}</td>
                          <td className="num">{money(unattributed.net)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {overview.byPlatform.length > 0 && (
                <p className="admin-note">
                  All-time sales by platform:{' '}
                  {overview.byPlatform.map(row => `${platformName(row.platform)} ${row.sales}`).join(', ')}.
                </p>
              )}
            </div>
          )}

          {tab === 'players' && (
            <ul className="jukebox-list">
              {overview.players.length === 0 && <p className="jukebox-empty">No jukeboxes yet.</p>}
              {overview.players.map(player => {
                const open = openPlayerId === player.id;
                const playing = player.nowPlaying && !isFillerVideo(player.nowPlaying) ? player.nowPlaying.title : null;
                const queueVideos = (open && queue && queue.queue && queue.queue.videos) || [];
                const nowPlaying = open && queue && !isFillerVideo(queue.nowPlayingVideo) ? queue.nowPlayingVideo : null;
                return (
                  <li key={player.id} className={open ? 'jukebox-row admin-player is-open' : 'jukebox-row admin-player'}>
                    <button type="button" className="admin-player-head" onClick={() => togglePlayer(player.id)} aria-expanded={open}>
                      <div className="jukebox-player">
                        <div className="admin-cell-main">{player.name}</div>
                        <div className="admin-code">{player.playerIdentifier.toUpperCase()}</div>
                      </div>
                      <div className="jukebox-row-meta">
                        <span>{player.owner ? `Owner ${player.owner.username}` : 'No owner'}</span>
                        <span>{player.queued === 1 ? '1 song queued' : `${player.queued} songs queued`}</span>
                        <span>Active {agoText(player.lastActive)}</span>
                      </div>
                      <div className="admin-now">{playing ? `Now playing: ${playing}` : 'Nothing playing'}</div>
                    </button>

                    {open && (
                      <div className="admin-queue">
                        {!queue && !queueError && <p className="jukebox-empty">Loading the queue…</p>}
                        {!!queueError && <p className="jukebox-error">{queueError}</p>}
                        {!!queue && (
                          <ul className="jukebox-list">
                            {!!nowPlaying && (
                              <JukeboxVideoRow
                                video={nowPlaying}
                                eyebrow="Now playing"
                                isBump={nowPlaying.isBump}
                                isSuperBump={nowPlaying.isSuperBump}
                                actions={[]}
                              />
                            )}
                            {queueVideos.length === 0 && <p className="jukebox-empty">Empty Queue</p>}
                            {queueVideos.map((video: any, index: number) => (
                              <JukeboxVideoRow
                                key={video.id}
                                video={video}
                                eyebrow={index === 0 ? 'Up next' : undefined}
                                isBump={video.isBump}
                                isSuperBump={video.isSuperBump}
                                actions={[]}
                              />
                            ))}
                          </ul>
                        )}
                        <Link className="jukebox-primary-link" to={`/control/${player.playerIdentifier}`}>
                          Open its control panel
                        </Link>
                        <button
                          type="button"
                          className="jukebox-primary-link admin-message-link"
                          onClick={() => {
                            setMessageTarget(String(player.id));
                            openTab('messages');
                          }}
                        >
                          Send it a message
                        </button>
                        <button
                          type="button"
                          className="jukebox-primary-link admin-message-link"
                          onClick={() => {
                            setReloadTarget(String(player.id));
                            openTab('reload');
                            reloadScreens(String(player.id));
                          }}
                        >
                          Reload its screens
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {tab === 'users' && (
            <div>
              <form className="jukebox-search" onSubmit={runSearch}>
                <input
                  type="search"
                  value={search}
                  placeholder="Name, email or id"
                  aria-label="Find a user"
                  onChange={event => setSearch(event.target.value)}
                />
                <button type="submit" disabled={searching || !search.trim()}>
                  Find
                </button>
              </form>
              {!!searchError && <p className="jukebox-error">{searchError}</p>}
              {users && users.length === 0 && <p className="jukebox-empty">Nobody matches that.</p>}
              {users && users.length === 25 && <p className="admin-note">Showing the first 25. Search more exactly to narrow it.</p>}
              <ul className="jukebox-list">
                {(users || []).map(user => {
                  const open = openUserId === user.id;
                  return (
                    <li key={user.id} className="jukebox-row admin-player">
                      <button type="button" className="admin-player-head" onClick={() => toggleUser(user)} aria-expanded={open}>
                        <div className="jukebox-player">
                          <div className="admin-cell-main">
                            {user.username}
                            {user.role === 'admin' && <span className="admin-badge">Admin</span>}
                            {!!user.shoutoutBannedAt && <span className="admin-badge admin-badge-banned">Shout-out banned</span>}
                            {!!user.plusUntil && <span className="admin-badge admin-badge-plus">Jookable+</span>}
                            {!!user.mfaEnabledAt && <span className="admin-badge">Two-step</span>}
                          </div>
                          <div className="admin-spend">{money(user.spend.gross)}</div>
                        </div>
                        <div className="jukebox-row-meta">
                          <span>{user.email}</span>
                          <span>#{user.id}</span>
                          <span>Joined {shortDate(user.createdAt)}</span>
                        </div>
                        <div className="jukebox-row-meta">
                          <span>
                            {user.spend.sales} {user.spend.sales === 1 ? 'purchase' : 'purchases'}
                            {user.spend.storeSales > 0 && ` (${user.spend.storeSales} in app stores)`}
                          </span>
                          <span>
                            Holds {user.availableBumps} bumps, {user.availableSuperBumps} super
                          </span>
                          {user.players > 0 && <span>Owns {user.players} {user.players === 1 ? 'jukebox' : 'jukeboxes'}</span>}
                          {!!user.plusUntil && <span>Jookable+ until {shortDate(user.plusUntil)}</span>}
                        </div>
                      </button>

                      {open && (
                        <div className="admin-queue">
                          {!!user.shoutoutBannedAt && (
                            <div className="admin-shoutout-ban">
                              <span>
                                Banned from shout-outs everywhere since {shortDate(user.shoutoutBannedAt)}, after bans on
                                players of three different owners.
                              </span>
                              <button type="button" disabled={liftingBan} onClick={() => liftShoutoutBan(user)}>
                                {liftingBan ? 'Saving…' : 'Unban'}
                              </button>
                            </div>
                          )}
                          {!!banError && <p className="jukebox-error">{banError}</p>}
                          {!!user.mfaEnabledAt && (
                            <div className="admin-shoutout-ban">
                              <span>Two-step sign-in on since {shortDate(user.mfaEnabledAt)}.</span>
                              <button type="button" disabled={mfaBusy} onClick={() => turnOffMfa(user)}>
                                {mfaBusy ? 'Saving…' : 'Turn off'}
                              </button>
                            </div>
                          )}
                          {!!mfaError && <p className="jukebox-error">{mfaError}</p>}
                          <form className="admin-bumps" onSubmit={event => saveBumps(event, user)}>
                            <label className="admin-bumps-field">
                              <span>Bumps</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                step={1}
                                value={bumpsInput}
                                onChange={event => {
                                  setBumpsInput(event.target.value);
                                  setBumpsSaved(false);
                                }}
                              />
                            </label>
                            <label className="admin-bumps-field">
                              <span>Super bumps</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                step={1}
                                value={superBumpsInput}
                                onChange={event => {
                                  setSuperBumpsInput(event.target.value);
                                  setBumpsSaved(false);
                                }}
                              />
                            </label>
                            <label className="admin-bumps-field admin-bumps-note">
                              <span>Reason (optional)</span>
                              <input
                                type="text"
                                maxLength={200}
                                value={bumpNote}
                                placeholder="Refund, gift, test…"
                                onChange={event => setBumpNote(event.target.value)}
                              />
                            </label>
                            <button
                              type="submit"
                              disabled={
                                savingBumps ||
                                (bumpsInput.trim() === String(user.availableBumps) &&
                                  superBumpsInput.trim() === String(user.availableSuperBumps))
                              }
                            >
                              {savingBumps ? 'Saving…' : 'Set bumps'}
                            </button>
                          </form>
                          {!!bumpsError && <p className="jukebox-error">{bumpsError}</p>}
                          {bumpsSaved && <p className="admin-note">Saved. Not counted as a sale.</p>}
                          {bumpChanges && bumpChanges.length > 0 && (
                            <ul className="admin-purchases">
                              {bumpChanges.map(change => (
                                <li key={change.id} className="admin-purchase">
                                  <span className="admin-purchase-date">{shortDate(change.createdAt)}</span>
                                  <span className="admin-purchase-name">
                                    Bumps {change.oldBumps} → {change.newBumps}, super {change.oldSuperBumps} →{' '}
                                    {change.newSuperBumps}
                                    <span className="admin-cell-sub">
                                      {' '}
                                      by {change.adminUsername || 'a deleted admin'}
                                      {change.note && ` · ${change.note}`}
                                    </span>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}

                          <h3 className="admin-heading">Purchases</h3>
                          {!purchases && <p className="jukebox-empty">Loading purchases…</p>}
                          {purchases && purchases.length === 0 && <p className="jukebox-empty">No purchases.</p>}
                          {purchases && purchases.length > 0 && (
                            <ul className="admin-purchases">
                              {purchases.map((purchase, index) => (
                                <li key={index} className="admin-purchase">
                                  <span className="admin-purchase-date">{shortDate(purchase.soldAt)}</span>
                                  <span className="admin-purchase-name">
                                    {purchase.productName}
                                    <span className="admin-cell-sub">
                                      {' '}
                                      {platformName(purchase.platform)}
                                      {purchase.player && ` · ${purchase.player.name}`}
                                    </span>
                                  </span>
                                  <span className="admin-purchase-amount">
                                    {purchase.amount === null ? 'Store' : money(purchase.amount)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {tab === 'rankings' && (
            <div>
              <div className="admin-period">
                <button
                  type="button"
                  className={rankBy === 'songs' ? 'admin-chip is-active' : 'admin-chip'}
                  onClick={() => setRankBy('songs')}
                >
                  Songs played
                </button>
                <button
                  type="button"
                  className={rankBy === 'spend' ? 'admin-chip is-active' : 'admin-chip'}
                  onClick={() => setRankBy('spend')}
                >
                  Money spent
                </button>
              </div>

              {!rankings && !rankingsError && <p className="jukebox-empty">Loading the rankings…</p>}
              {!!rankingsError && <p className="jukebox-error">{rankingsError}</p>}
              {!!rankings && (
                <RankingTable
                  users={rankBy === 'songs' ? rankings.bySongsPlayed : rankings.bySpend}
                  rankBy={rankBy}
                  money={money}
                />
              )}
              {!!rankings && (
                <p className="admin-note">
                  {rankings.playsSince
                    ? `Songs played are counted from ${shortDate(rankings.playsSince)}, when Jookable started keeping them.`
                    : 'Songs played are counted from now on. Nothing kept them before.'}{' '}
                  The jukebox is the one a person's songs have played on most. Top 50.
                  {rankBy === 'spend' && ' Web sales only; app store sales are counted but not priced.'}
                </p>
              )}
            </div>
          )}

          {tab === 'reload' && (
            <div>
              <p className="admin-note">
                Reloads the TVs and phones connected to a jukebox, so they pick up the newest version of the site. Each
                TV waits a random few seconds first and then carries on with the song where it was. A phone waits until
                it is put away or switched from, so nobody loses what they were typing.
              </p>
              <form
                className="admin-message-form"
                onSubmit={event => {
                  event.preventDefault();
                  reloadScreens(reloadTarget);
                }}
              >
                <label className="admin-bumps-field admin-message-target">
                  <span>Reload</span>
                  <select value={reloadTarget} onChange={event => setReloadTarget(event.target.value)}>
                    <option value={EVERY_JUKEBOX}>Every jukebox ({overview.players.length})</option>
                    {overview.players
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(player => (
                        <option key={player.id} value={String(player.id)}>
                          {player.name} · {player.playerIdentifier.toUpperCase()}
                        </option>
                      ))}
                  </select>
                </label>
                <button type="submit" disabled={reloading}>
                  {reloading ? 'Reloading…' : reloadTarget === EVERY_JUKEBOX ? 'Reload every screen' : 'Reload its screens'}
                </button>
              </form>
              {!!reloadError && <p className="jukebox-error">{reloadError}</p>}
              {!!reloadResult && <p className="admin-note">{reloadResult}</p>}
              <p className="admin-note">
                You rarely need this: after an update, open screens notice the new version and reload on their own.
              </p>
            </div>
          )}

          {tab === 'messages' && (
            <div>
              <p className="admin-note">
                Shows big across the top of the TV, above the video, for the seconds you choose, then scrolls in the
                ticker for the usual shout-out time. Free, and it works whatever the venue's shout-out setting is.
              </p>
              <form className="admin-message-form" onSubmit={sendMessage}>
                <label className="admin-bumps-field admin-message-target">
                  <span>Send to</span>
                  <select value={messageTarget} onChange={event => setMessageTarget(event.target.value)}>
                    <option value={EVERY_JUKEBOX}>Every jukebox ({overview.players.length})</option>
                    {overview.players
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(player => (
                        <option key={player.id} value={String(player.id)}>
                          {player.name} · {player.playerIdentifier.toUpperCase()}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="admin-bumps-field admin-message-seconds">
                  <span>Seconds</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={ADMIN_MESSAGE_MIN_SECONDS}
                    max={ADMIN_MESSAGE_MAX_SECONDS}
                    step={1}
                    value={messageSeconds}
                    onChange={event => setMessageSeconds(event.target.value)}
                  />
                </label>
                <label className="admin-bumps-field admin-message-text-field">
                  <span>
                    Message ({Array.from(messageText).length}/{ADMIN_MESSAGE_MAX_LENGTH})
                  </span>
                  <textarea
                    rows={3}
                    maxLength={ADMIN_MESSAGE_MAX_LENGTH * 2}
                    value={messageText}
                    placeholder="Last call in 15 minutes!"
                    onChange={event => {
                      setMessageText(event.target.value);
                      setMessageSent(null);
                    }}
                  />
                </label>
                <button
                  type="submit"
                  disabled={
                    sendingMessage || !messageText.trim() || Array.from(messageText.trim()).length > ADMIN_MESSAGE_MAX_LENGTH
                  }
                >
                  {sendingMessage ? 'Sending…' : messageTarget === EVERY_JUKEBOX ? 'Send to every jukebox' : 'Send'}
                </button>
              </form>
              {!!sendError && <p className="jukebox-error">{sendError}</p>}
              {!!messageSent && <p className="admin-note">{messageSent}</p>}

              <h3 className="admin-heading">Recent messages</h3>
              {!sentMessages && !messagesError && <p className="jukebox-empty">Loading messages…</p>}
              {!!messagesError && <p className="jukebox-error">{messagesError}</p>}
              {sentMessages && sentMessages.length === 0 && <p className="jukebox-empty">None sent yet.</p>}
              {sentMessages && sentMessages.length > 0 && (
                <ul className="admin-purchases">
                  {sentMessages.map(message => (
                    <li key={message.id} className="admin-purchase">
                      <span className="admin-purchase-date">{agoText(message.createdAt)}</span>
                      <span className="admin-purchase-name">
                        {message.message}
                        <span className="admin-cell-sub">
                          {' '}
                          {message.everyone ? 'Every jukebox' : message.player ? message.player.name : 'A deleted jukebox'} ·{' '}
                          {message.seconds}s{message.sentBy && ` · by ${message.sentBy}`}
                        </span>
                      </span>
                      {message.msLeft > 0 ? (
                        <button type="button" className="admin-message-stop" onClick={() => stopMessage(message)}>
                          Stop ({Math.ceil(message.msLeft / 1000)}s left)
                        </button>
                      ) : (
                        <span className="admin-purchase-amount">Done</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="footer">
        <FooterContent />
      </div>
    </div>
  );
};

export default AdminView;
